import { useState, useMemo, useCallback } from 'react';

/**
 * useResultsFilter - Search and filter responses with server-side rendering
 * Handles participant search, answer filtering, and sorting
 */
export const useResultsFilter = (responses = []) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all' | 'text' | 'option'
    const [sortBy, setSortBy] = useState('recent'); // 'recent' | 'oldest' | 'name'

    // Filter and search responses
    const filteredResponses = useMemo(() => {
        let filtered = [...responses];

        // Search by participant name
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(r =>
                (r.participant || '').toLowerCase().includes(term)
            );
        }

        // Filter by answer type
        if (filterType !== 'all') {
            filtered = filtered.filter(r => {
                if (filterType === 'text') {
                    return typeof r.answer === 'string';
                }
                if (filterType === 'option') {
                    return typeof r.answer === 'number' || Array.isArray(r.answer);
                }
                return true;
            });
        }

        // Sort
        filtered.sort((a, b) => {
            if (sortBy === 'recent') {
                return new Date(b.created_at) - new Date(a.created_at);
            } else if (sortBy === 'oldest') {
                return new Date(a.created_at) - new Date(b.created_at);
            } else if (sortBy === 'name') {
                return (a.participant || '').localeCompare(b.participant || '');
            }
            return 0;
        });

        return filtered;
    }, [responses, searchTerm, filterType, sortBy]);

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setFilterType('all');
        setSortBy('recent');
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        filterType,
        setFilterType,
        sortBy,
        setSortBy,
        filteredResponses,
        resultCount: filteredResponses.length,
        totalCount: responses.length,
        clearFilters,
        hasActiveFilters: searchTerm.trim() !== '' || filterType !== 'all' || sortBy !== 'recent',
    };
};
